import { useEffect, useState } from "react";

export const defaultStoredTheme = {
  language: "en",
  logo: {
    light: "",
    dark: "",
  },
  wideLogo: {
    light: "",
    dark: "",
  },
  button: {
    primary: {
      backgroundColor: {
        light: "#24ae81",
        dark: "#04543E",
      },
      hoverBackgroundColor: {
        light: "#1d9670",
        dark: "#2bc492",
      },
      textColor: {
        light: "#f1f9f7",
        dark: "#f1f5f9",
      },
      hoverTextColor: {
        light: "#ffffff",
        dark: "#ffffff",
      },
      borderColor: {
        light: "#24ae81",
        dark: "#04543E",
      },
      hoverBorderColor: {
        light: "#1d9670",
        dark: "#2bc492",
      },
    },
    secondary: {
      backgroundColor: {
        light: "#B7EFDA",
        dark: "#2A403A",
      },
      hoverBackgroundColor: {
        light: "#A0E5CD",
        dark: "#364D46",
      },
      textColor: {
        light: "#2A403A",
        dark: "#C2E6D9",
      },
      hoverTextColor: {
        light: "#1E302B",
        dark: "#CAF5EA",
      },
      borderColor: {
        light: "#B7EFDA",
        dark: "#2A403A",
      },
      hoverBorderColor: {
        light: "#A0E5CD",
        dark: "#364D46",
      },
    },
    success: {
      backgroundColor: {
        light: "#24ae81",
        dark: "#24ae81",
      },
      hoverBackgroundColor: {
        light: "#1d9670",
        dark: "#2bc492",
      },
      textColor: {
        light: "#f1f9f7",
        dark: "#f1f5f9",
      },
      hoverTextColor: {
        light: "#ffffff",
        dark: "#ffffff",
      },
      borderColor: {
        light: "#24ae81",
        dark: "#24ae81",
      },
      hoverBorderColor: {
        light: "#1d9670",
        dark: "#2bc492",
      },
    },
    error: {
      backgroundColor: {
        light: "#8e1e1e",
        dark: "#8e1e1e",
      },
      hoverBackgroundColor: {
        light: "#7a1919",
        dark: "#a52323",
      },
      textColor: {
        light: "#ffbdbd",
        dark: "#ffbdbd",
      },
      hoverTextColor: {
        light: "#ffffff",
        dark: "#ffd0d0",
      },
      borderColor: {
        light: "#8e1e1e",
        dark: "#8e1e1e",
      },
      hoverBorderColor: {
        light: "#7a1919",
        dark: "#a52323",
      },
    },
  },
  input: {
    backgroundColor: {
      light: "#f2f2f2",
      dark: "#080F11",
    },
    textColor: {
      light: "#080F11",
      dark: "#F1F5F9",
    },
    borderRadius: {
      light: "8px",
      dark: "8px",
    },
    borderColor: {
      light: "#E0E9E6",
      dark: "#262C2E",
    },
  },
  card: {
    backgroundColor: {
      light: "#FBFCFC",
      dark: "#141A1B",
    },
    borderRadius: {
      light: "10px",
      dark: "10px",
    },
    borderColor: {
      light: "#e0e9e6",
      dark: "#141a1b",
    },
  },
  sidebar: {
    backgroundColor: {
      light: "#ffffff",
      dark: "#080F11",
    },
    textColor: {
      light: "#080f11",
      dark: "#b8f2e2",
    },
    hoverBackgroundColor: {
      light: "#f1f5f9",
      dark: "#2e3334",
    },
    hoverTextColor: {
      light: "#24ae81",
      dark: "#b8f2e2",
    },
    activeLinkBackgroundColor: {
      light: "#b8f2e2",
      dark: "#2f544a",
    },
    activeLinkTextColor: {
      light: "#043225",
      dark: "#b8f2e2",
    },
  },
  general: {
    backgroundColor: {
      light: "#fbfcfc",
      dark: "#080F11",
    },
    textColor: {
      light: "#080f11",
      dark: "#f1f5f9",
    },
  },
  slider: {
    backgroundColor: {
      light: "#24ae81",
      dark: "#24ae81",
    },
  },
  radiobutton: {
    backgroundColor: {
      light: "#24ae81",
      dark: "#24ae81",
    },
  },
  checkBox: {
    backgroundColor: {
      light: "#24ae81",
      dark: "#24ae81",
    },
  },
  inputSwitch: {
    backgroundColor: {
      light: "#24ae81",
      dark: "#24ae81",
    },
  },
  header: {
    backgroundColor: {
      light: "#ffffff",
      dark: "#080F11",
    },
    textColor: {
      light: "#080f11",
      dark: "#f1f5f9",
    },
    iconColor: {
      light: "#758480",
      dark: "#7ec7bd",
    },
  },
  footer: {
    backgroundColor: {
      light: "#ffffff",
      dark: "#080F11",
    },
    textColor: {
      light: "#080f11",
      dark: "#f1f5f9",
    },
  },
  playground: {
    infoBg: {
      light: "#f0f4f8",
      dark: "#080F11",
    },
    infoText: {
      light: "#6b7280",
      dark: "#B1B1B1",
    },
    iconBg: {
      light: "#e0e9e6",
      dark: "#2E3334",
    },
    userMessageBg: {
      light: "rgba(0, 0, 0, 0.04)",
      dark: "#ffffff14",
    },
    inputPlaceholder: {
      light: "#9ca3af99",
      dark: "#9ca3af99",
    },
  },
};

const LEGACY_LIGHT_USER_MESSAGE_BG = "#000000";

const mergeThemeSection = (defaults, overrides) => {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return structuredClone(defaults);
  }

  const merged = structuredClone(defaults);

  Object.entries(overrides).forEach(([key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      key in merged &&
      merged[key] &&
      typeof merged[key] === "object" &&
      !Array.isArray(merged[key])
    ) {
      merged[key] = mergeThemeSection(merged[key], value);
      return;
    }

    merged[key] = value;
  });

  return merged;
};

const normalizeStoredTheme = (theme) => {
  const mergedTheme = mergeThemeSection(defaultStoredTheme, theme);

  if (
    mergedTheme?.playground?.userMessageBg?.light?.trim?.().toLowerCase() ===
    LEGACY_LIGHT_USER_MESSAGE_BG
  ) {
    mergedTheme.playground.userMessageBg.light =
      defaultStoredTheme.playground.userMessageBg.light;
  }

  return mergedTheme;
};

export const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem("currentOrganizationTheme");
    return stored
      ? normalizeStoredTheme(JSON.parse(stored))
      : defaultStoredTheme;
  } catch {
    return defaultStoredTheme;
  }
};

const setCssVariable = (name, value) => {
  if (value !== undefined && value !== null) {
    document.body.style.setProperty(name, value);
  }
};

const parseCssColor = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const color = value.trim();
  if (!color || color.startsWith("var(")) {
    return null;
  }

  if (color.startsWith("#")) {
    const hex = color.slice(1);

    if (hex.length === 3 || hex.length === 4) {
      const [r, g, b, a = "f"] = hex.split("");
      return {
        r: parseInt(`${r}${r}`, 16),
        g: parseInt(`${g}${g}`, 16),
        b: parseInt(`${b}${b}`, 16),
        a: parseInt(`${a}${a}`, 16) / 255,
      };
    }

    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;

      return { r, g, b, a };
    }
  }

  const rgbMatch = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i,
  );

  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
      a: rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]),
    };
  }

  return null;
};

const compositeColors = (foreground, background) => {
  const alpha = foreground.a ?? 1;

  if (alpha >= 1) {
    return foreground;
  }

  return {
    r: foreground.r * alpha + background.r * (1 - alpha),
    g: foreground.g * alpha + background.g * (1 - alpha),
    b: foreground.b * alpha + background.b * (1 - alpha),
    a: 1,
  };
};

export const applyStoredThemeMode = (theme, mode) => {
  const resolvedTheme = theme || defaultStoredTheme;
  const resolvedMode = mode || localStorage.getItem("themeMode") || "dark";
  const generalBackground =
    resolvedTheme.general.backgroundColor[resolvedMode] ||
    defaultStoredTheme.general.backgroundColor[resolvedMode];
  const userMessageBackground =
    resolvedTheme?.playground?.userMessageBg?.[resolvedMode] ??
    defaultStoredTheme.playground.userMessageBg[resolvedMode];

  setCssVariable("--input", resolvedTheme.input.backgroundColor[resolvedMode]);
  setCssVariable("--input-text", resolvedTheme.input.textColor[resolvedMode]);
  setCssVariable(
    "--input-border",
    resolvedTheme.input.borderColor[resolvedMode],
  );
  setCssVariable(
    "--background",
    resolvedTheme.general.backgroundColor[resolvedMode],
  );
  setCssVariable(
    "--primary",
    resolvedTheme.button.primary.backgroundColor[resolvedMode],
  );
  setCssVariable(
    "--secondary",
    resolvedTheme.button.secondary.backgroundColor[resolvedMode],
  );
  setCssVariable(
    "--card-background",
    resolvedTheme.card.backgroundColor[resolvedMode],
  );
  setCssVariable("--card", resolvedTheme.card.backgroundColor[resolvedMode]);
  setCssVariable(
    "--card-foreground",
    resolvedTheme.general.textColor[resolvedMode],
  );
  setCssVariable("--border", resolvedTheme.card.borderColor[resolvedMode]);

  setCssVariable(
    "--playground-info-bg",
    resolvedTheme?.playground?.infoBg?.[resolvedMode] ??
      defaultStoredTheme.playground.infoBg[resolvedMode],
  );
  setCssVariable(
    "--playground-info-text",
    resolvedTheme?.playground?.infoText?.[resolvedMode] ??
      defaultStoredTheme.playground.infoText[resolvedMode],
  );
  setCssVariable(
    "--playground-icon-bg",
    resolvedTheme?.playground?.iconBg?.[resolvedMode] ??
      defaultStoredTheme.playground.iconBg[resolvedMode],
  );
  setCssVariable("--user-message-bg", userMessageBackground);
  setCssVariable(
    "--user-message-text",
    getContrastColor(userMessageBackground, generalBackground),
  );
  setCssVariable(
    "--input-placeholder",
    resolvedTheme?.playground?.inputPlaceholder?.[resolvedMode] ??
      defaultStoredTheme.playground.inputPlaceholder[resolvedMode],
  );

  // ---------------------------------------------------------------------------
  // Design-system tokens.
  //
  // tailwind.config.js maps utility classes onto 38 custom properties. Until now
  // this function wrote 7 of them, so 31 were pinned to the static defaults in
  // index.css and a white-label org's brand stopped at whichever tokens happened
  // to be written.
  //
  // Every mapping below reads a value the stored theme ALREADY carries — none of
  // it is derived or invented. That is why --primary-foreground was a real bug
  // rather than a missing feature: button.primary.textColor has always existed,
  // it simply was not written anywhere. An org that set a light brand primary got
  // the static near-white foreground and unreadable button text.
  // ---------------------------------------------------------------------------

  setCssVariable("--foreground", resolvedTheme.general.textColor[resolvedMode]);

  // The bug: an org's configured button text colour never reached the token that
  // shadcn's Button actually renders with.
  setCssVariable(
    "--primary-foreground",
    resolvedTheme.button.primary.textColor[resolvedMode],
  );
  setCssVariable(
    "--secondary-foreground",
    resolvedTheme.button.secondary.textColor[resolvedMode],
  );

  // Destructive maps to the error button, which is the only red the stored theme
  // models.
  setCssVariable(
    "--destructive",
    resolvedTheme.button.error.backgroundColor[resolvedMode],
  );
  setCssVariable(
    "--destructive-foreground",
    resolvedTheme.button.error.textColor[resolvedMode],
  );

  // Popovers are cards as far as the stored theme is concerned.
  setCssVariable("--popover", resolvedTheme.card.backgroundColor[resolvedMode]);
  setCssVariable(
    "--popover-foreground",
    resolvedTheme.general.textColor[resolvedMode],
  );

  // Sidebar. This is the set that dies silently the day MUI is deleted: today the
  // sidebar is themed through createTheme in contexts/ThemeContext.jsx reading
  // the same storedTheme.sidebar.* values, and nothing else writes these tokens.
  // Nothing renders them yet (zero bg-sidebar/text-sidebar usages), so writing
  // them now is forward-looking and cannot regress the current UI.
  setCssVariable("--sidebar", resolvedTheme.sidebar.backgroundColor[resolvedMode]);
  setCssVariable(
    "--sidebar-foreground",
    resolvedTheme.sidebar.textColor[resolvedMode],
  );
  // shadcn's sidebar "accent" is the hover state; "primary" is the active item.
  setCssVariable(
    "--sidebar-accent",
    resolvedTheme.sidebar.hoverBackgroundColor[resolvedMode],
  );
  setCssVariable(
    "--sidebar-accent-foreground",
    resolvedTheme.sidebar.hoverTextColor[resolvedMode],
  );
  setCssVariable(
    "--sidebar-primary",
    resolvedTheme.sidebar.activeLinkBackgroundColor[resolvedMode],
  );
  setCssVariable(
    "--sidebar-primary-foreground",
    resolvedTheme.sidebar.activeLinkTextColor[resolvedMode],
  );
  // The stored theme has no sidebar-specific border or ring, so these follow the
  // card border and the primary colour rather than being left to static defaults.
  setCssVariable(
    "--sidebar-border",
    resolvedTheme.card.borderColor[resolvedMode],
  );
  setCssVariable(
    "--sidebar-ring",
    resolvedTheme.button.primary.backgroundColor[resolvedMode],
  );

  document.documentElement.style.setProperty(
    "--initial-background-color",
    resolvedTheme.general.backgroundColor[resolvedMode],
  );
  document.documentElement.style.setProperty(
    "--initial-text-color",
    resolvedTheme.general.textColor[resolvedMode],
  );
};

export const setStoredTheme = (theme) => {
  const normalizedTheme = normalizeStoredTheme(theme);
  const savedMode = localStorage.getItem("themeMode") || "dark";
  applyStoredThemeMode(normalizedTheme, savedMode);

  localStorage.setItem(
    "currentOrganizationTheme",
    JSON.stringify(normalizedTheme),
  );

  // 🔔 notify same-tab listeners
  window.dispatchEvent(
    new CustomEvent("theme-updated", {
      detail: normalizedTheme,
    }),
  );
};

export const getContrastColor = (colorValue, backgroundColor = "#ffffff") => {
  const parsedColor = parseCssColor(colorValue);
  const parsedBackground = parseCssColor(backgroundColor) || {
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  };

  if (!parsedColor) {
    return "#FFFFFF";
  }

  const effectiveColor = compositeColors(parsedColor, parsedBackground);
  const luminance =
    (0.299 * effectiveColor.r +
      0.587 * effectiveColor.g +
      0.114 * effectiveColor.b) /
    255;

  // Return black for bright bg, white for dark bg
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

export const useStoredTheme = () => {
  const [theme, setTheme] = useState(getStoredTheme);

  useEffect(() => {
    const handler = (e) => {
      setTheme(e.detail); // same-tab update
    };

    const storageHandler = (e) => {
      if (e.key === "currentOrganizationTheme") {
        setTheme(normalizeStoredTheme(JSON.parse(e.newValue))); // other tabs
      }
    };

    window.addEventListener("theme-updated", handler);
    window.addEventListener("storage", storageHandler);

    return () => {
      window.removeEventListener("theme-updated", handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, []);

  return theme;
};
